import { QuotationWorkbookDownload } from "@/components/insights/QuotationWorkbookDownload";
import type { ReactNode } from "react";
import { whatsappUrl } from "@/lib/content";
import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";

export const costProcessIraqMeta: InsightMeta = {
  slug: "iso-certification-cost-and-process-in-iraq",
  metaTitle: {
    en: "ISO Certification in Iraq: Quotation Scope & Process",
    ar: "شهادة الأيزو في العراق: نطاق عرض السعر والخطوات",
  },
  title: {
    en: "ISO Certification in Iraq: Cost, Process, and Timeline",
    ar: "شهادة الأيزو في العراق: التكلفة والإجراءات والمدة",
  },
  description: {
    en: "Plan a private ISO certification quotation for your Iraqi business: scope, preparation, assessment arrangements, timing and buyer acceptance.",
    ar: "حضّر طلب عرض سعر خاصاً لشركتك في العراق: النطاق والتحضير وترتيبات التقييم والمدة وشروط قبول المشتري.",
  },
  datePublished: "2026-07-21",
  dateModified: "2026-10-23",
  dateLabel: { en: "23 October 2026", ar: "23 تشرين الأول 2026" },
  enquiryMessage: {
    en: "Hello IraqCert, I read your ISO cost and process guide and would like a quotation for preparation support.\nOptional details — activity, buyer requirement and target date:\nPage: /insights/iso-certification-cost-and-process-in-iraq",
    ar: "مرحباً عراق سيرت، قرأت دليل تكلفة وإجراءات شهادة الأيزو وأرغب في عرض سعر لدعم التحضير.\nتفاصيل اختيارية — النشاط ومتطلب المشتري والموعد المستهدف:\nالصفحة: /ar/insights/iso-certification-cost-and-process-in-iraq",
  },
  faq: [
    {
      q: {
        en: "How much does ISO certification cost in Iraq?",
        ar: "كم تكلفة شهادة الأيزو في العراق؟",
      },
      a: {
        en: "We prepare a private quotation after reviewing your activities, sites, workforce, system readiness and buyer requirement. Ask for separate preparation and certification-body items, plus exclusions and any future follow-up costs.",
        ar: "نُعد عرض سعر خاصاً بعد مراجعة الأنشطة والمواقع والقوى العاملة وجاهزية النظام ومتطلب المشتري. اطلب فصل أعمال التحضير عن أعمال جهة منح الشهادات، مع توضيح الاستثناءات وأي تكاليف لاحقة للمتابعة.",
      },
    },
    {
      q: {
        en: "How long does ISO certification take in Iraq?",
        ar: "كم تستغرق شهادة الأيزو في العراق؟",
      },
      a: {
        en: "Timing depends on preparation gaps, access to records, the proposed scope and the certification body’s schedule and findings. Confirm a workable plan after review; a deadline alone does not establish readiness.",
        ar: "تعتمد المدة على فجوات التحضير وإتاحة السجلات والنطاق المقترح وجدول جهة منح الشهادات ونتائج تقييمها. اتفق على خطة قابلة للتنفيذ بعد المراجعة؛ فوجود موعد نهائي لا يثبت الجاهزية.",
      },
    },
    {
      q: {
        en: "What is the ISO certification process?",
        ar: "ما هي خطوات الحصول على شهادة الأيزو؟",
      },
      a: {
        en: "Begin with the buyer requirement and scope. Preparation may include gap review, documentation, implementation, internal audit and management review. The certification body defines its assessment arrangements and decides whether to issue the certificate. Confirm validity and follow-up requirements with that body.",
        ar: "ابدأ بمتطلب المشتري والنطاق. قد يشمل التحضير مراجعة الفجوات والتوثيق والتطبيق والتدقيق الداخلي ومراجعة الإدارة. تحدّد جهة منح الشهادات ترتيبات تقييمها وتتخذ قرار الإصدار. أكّد معها مدة السريان ومتطلبات المتابعة.",
      },
    },
    {
      q: {
        en: "Do I need an accredited ISO certificate for Iraqi tenders?",
        ar: "هل أحتاج إلى شهادة أيزو معتمدة للمناقصات العراقية؟",
      },
      a: {
        en: "Read the specific tender or buyer requirement. If it expressly requires accredited certification, a non-accredited certificate does not satisfy that condition. Confirm the proposed issuer, its relevant accreditation scope and the buyer’s acceptance before commitment.",
        ar: "اقرأ متطلب المناقصة أو المشتري المعني. إذا اشترط صراحةً شهادة من جهة معتمدة، فلن تستوفي الشهادة غير المعتمدة ذلك الشرط. تحقّق من الجهة المانحة المقترحة ونطاق اعتمادها ذي الصلة وقبول المشتري قبل الالتزام.",
      },
    },
    {
      q: {
        en: "Can a small company in Iraq get ISO certified?",
        ar: "هل يمكن لشركة صغيرة في العراق الحصول على شهادة الأيزو؟",
      },
      a: {
        en: "Company size alone does not decide suitability. Describe the activities, people and sites to be covered so the proposed scope and work can be assessed. Do not assume a fixed lower fee or shorter schedule from headcount alone.",
        ar: "لا يحدّد حجم الشركة وحده ملاءمة المسار. صف الأنشطة والعاملين والمواقع المطلوب شمولها لتقييم النطاق والعمل المقترح. ولا تفترض أتعاباً أقل أو مدة أقصر اعتماداً على عدد العاملين وحده.",
      },
    },
    {
      q: {
        en: "How fast can I get ISO certified for a tender deadline in Iraq?",
        ar: "ما مدى سرعة الحصول على شهادة الأيزو قبل موعد مناقصة في العراق؟",
      },
      a: {
        en: "Share the actual deadline and buyer wording at the outset. A readiness review and the certification body’s availability determine whether a proposed plan is realistic. Verify certificate details with the issuer and relevant accreditation body; a missing database result alone should not be treated as a final verdict.",
        ar: "شارك الموعد النهائي ونص متطلب المشتري منذ البداية. تحدّد مراجعة الجاهزية وتوافر جهة منح الشهادات مدى واقعية الخطة. تحقّق من تفاصيل الشهادة لدى الجهة المانحة وهيئة الاعتماد المعنية؛ ولا تعتبر غياب نتيجة في قاعدة بيانات وحده حكماً نهائياً.",
      },
    },
  ],
};

// --- small prose helpers, styled to match the premium look ---
function H2({ children }: { children: ReactNode }) {
  return <h2 className="mt-12 text-2xl font-extrabold text-foreground sm:text-3xl">{children}</h2>;
}
function P({ children }: { children: ReactNode }) {
  return <p className="mt-4 text-base leading-relaxed text-muted-foreground">{children}</p>;
}

const costDrivers = [
  {
    driver: { en: "Company size", ar: "حجم الشركة" },
    effect: {
      en: "Workforce and the nature of operations inform the body’s assessment planning.",
      ar: "يساعد عدد العاملين وطبيعة العمليات جهة منح الشهادات في تخطيط التقييم.",
    },
  },
  {
    driver: { en: "Number of sites", ar: "عدد المواقع" },
    effect: {
      en: "Each location may require its own audit.",
      ar: "قد يحتاج كل موقع إلى تدقيق خاص به.",
    },
  },
  {
    driver: { en: "The standard", ar: "المعيار" },
    effect: {
      en: "Some standards need more specialised auditor time.",
      ar: "بعض المعايير تتطلب وقت تدقيق متخصص أكثر.",
    },
  },
  {
    driver: { en: "Scope of activities", ar: "نطاق الأنشطة" },
    effect: {
      en: "Describe the activities precisely so preparation and assessment work can be scoped.",
      ar: "صف الأنشطة بدقة لتحديد أعمال التحضير والتقييم.",
    },
  },
  {
    driver: { en: "Readiness", ar: "الجاهزية" },
    effect: {
      en: "Review existing procedures and records to identify actual preparation gaps.",
      ar: "راجع الإجراءات والسجلات القائمة لتحديد فجوات التحضير الفعلية.",
    },
  },
];

const processSteps = [
  {
    stage: { en: "1. Gap analysis", ar: "1. تحليل الفجوات" },
    what: {
      en: "Your current system is measured against the standard.",
      ar: "يُقاس نظامك الحالي مقابل المعيار.",
    },
    output: { en: "A gap report and action plan", ar: "تقرير فجوات وخطة عمل" },
  },
  {
    stage: { en: "2. Documentation", ar: "2. التوثيق" },
    what: {
      en: "You build the required policies and procedures.",
      ar: "تبني السياسات والإجراءات المطلوبة.",
    },
    output: {
      en: "Documented processes and implementation records",
      ar: "عمليات موثّقة وسجلات تطبيق",
    },
  },
  {
    stage: { en: "3. Internal audit", ar: "3. التدقيق الداخلي" },
    what: {
      en: "Review implementation and record findings before external assessment.",
      ar: "راجع التطبيق وسجّل النتائج قبل التقييم الخارجي.",
    },
    output: { en: "Corrected non-conformities", ar: "تصحيح حالات عدم المطابقة" },
  },
  {
    stage: { en: "4. Stage 1 audit", ar: "4. تدقيق المرحلة الأولى" },
    what: {
      en: "The certification body reviews your documentation and readiness.",
      ar: "تراجع الجهة المانحة وثائقك وجاهزيتك.",
    },
    output: {
      en: "Readiness findings and any issues to address",
      ar: "نتائج مراجعة الجاهزية والمسائل المطلوب معالجتها",
    },
  },
  {
    stage: { en: "5. Stage 2 audit", ar: "5. تدقيق المرحلة الثانية" },
    what: {
      en: "On-site verification that the system works in practice.",
      ar: "تحقق ميداني من عمل النظام فعلياً.",
    },
    output: { en: "Audit findings", ar: "نتائج التدقيق" },
  },
  {
    stage: { en: "6. Certification", ar: "6. إصدار الشهادة" },
    what: {
      en: "The certification body reviews the assessment and makes its decision.",
      ar: "تراجع جهة منح الشهادات نتائج التقييم وتتخذ قرارها.",
    },
    output: {
      en: "Certification decision and stated conditions",
      ar: "قرار منح الشهادة والشروط المحدّدة",
    },
  },
  {
    stage: { en: "7. Surveillance", ar: "7. المتابعة الرقابية" },
    what: {
      en: "Follow the certification body’s agreed surveillance and renewal arrangements.",
      ar: "التزم بترتيبات المتابعة والتجديد المتفق عليها مع جهة منح الشهادات.",
    },
    output: {
      en: "Follow-up records and current certificate status",
      ar: "سجلات المتابعة وحالة الشهادة الحالية",
    },
  },
];

export function CostProcessIraqBody() {
  const { t } = useLanguage();
  const th = "px-4 py-3 text-start text-xs font-bold uppercase tracking-wider text-foreground";
  const td = "px-4 py-3 text-start text-sm text-muted-foreground align-top";

  return (
    <>
      <P>
        {t({
          en: "A useful quotation begins with your business requirement. Describe the activities and sites to be covered, the system already in place and the buyer’s acceptance conditions. This guide explains what to clarify before agreeing preparation and certification work; fees are provided privately for the agreed scope.",
          ar: "يبدأ عرض السعر المفيد بمتطلب شركتك. حدّد الأنشطة والمواقع المطلوب شمولها والنظام القائم وشروط قبول المشتري. يوضح هذا الدليل ما ينبغي تأكيده قبل الاتفاق على أعمال التحضير ومنح الشهادة؛ وتُقدَّم الأتعاب بصورة خاصة وفق النطاق المتفق عليه.",
        })}
      </P>
      <P>
        {t({
          en: "Before requesting a quotation, use our guide to ",
          ar: "قبل طلب عرض سعر، راجع دليل ",
        })}
        <LangLink
          to="/insights/which-iso-standard-does-my-company-need"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "choosing the ISO standard your company needs",
            ar: "اختيار معيار الأيزو المناسب لشركتك",
          })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>
      <a
        href={`${whatsappUrl}?text=${encodeURIComponent(t(costProcessIraqMeta.enquiryMessage!))}`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:brightness-110"
      >
        {t({ en: "Request a scoped quotation", ar: "اطلب عرض سعر وفق احتياجك" })}
      </a>
      <P>
        {t({ en: "For the full picture, start with ", ar: "للصورة الكاملة، ابدأ من " })}
        <LangLink to="/" className="font-semibold text-primary hover:underline">
          {t({ en: "ISO certification in Iraq", ar: "شهادة الأيزو في العراق" })}
        </LangLink>
        {t({
          en: ". If you already know you need quality certification, see ",
          ar: ". وإذا كنت تعلم أنك تحتاج شهادة الجودة، فاطّلع على ",
        })}
        <LangLink to="/services/iso-9001" className="font-semibold text-primary hover:underline">
          {t({ en: "ISO 9001 certification", ar: "شهادة الأيزو 9001" })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>

      <H2>
        {t({
          en: "How much does ISO certification cost in Iraq?",
          ar: "كم تكلفة شهادة الأيزو في العراق؟",
        })}
      </H2>
      <P>
        {t({
          en: "Pricing depends on your activities, sites, system readiness and the work agreed. Request a written scope that identifies preparation support, certification-body assessment, follow-up arrangements and exclusions. Share the activity, sites, approximate workforce, current certificate or system, buyer wording and deadline. Do not send sensitive documents in an initial enquiry.",
          ar: "يعتمد السعر على أنشطة شركتك ومواقعها وجاهزية نظامها والعمل المتفق عليه. اطلب نطاق عمل مكتوباً يحدّد دعم التحضير والتقييم الذي تجريه جهة منح الشهادات وترتيبات المتابعة والاستثناءات. اذكر النشاط والمواقع والعدد التقريبي للعاملين والشهادة أو النظام الحالي ونص متطلب المشتري والموعد النهائي. لا ترسل وثائق حساسة في الاستفسار الأولي.",
        })}
      </P>
      <P>
        {t({ en: "Include ", ar: "ناقش أيضاً " })}
        <LangLink
          to="/insights/after-iso-certification-surveillance-audits-and-renewal"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({ en: "surveillance audits and renewal", ar: "تدقيق المتابعة وتجديد الشهادة" })}
        </LangLink>
        {t({ en: " when discussing the ongoing scope.", ar: " عند تحديد نطاق الخدمة المستمرّة." })}
      </P>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
        <table className="w-full border-collapse">
          <thead className="bg-accent">
            <tr>
              <th className={th}>{t({ en: "Cost driver", ar: "عامل التكلفة" })}</th>
              <th className={th}>{t({ en: "Effect on cost", ar: "الأثر على التكلفة" })}</th>
            </tr>
          </thead>
          <tbody>
            {costDrivers.map((row) => (
              <tr key={row.driver.en} className="border-t border-border">
                <td className={`${td} font-semibold text-foreground`}>{t(row.driver)}</td>
                <td className={td}>{t(row.effect)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <QuotationWorkbookDownload variant="cost" />

      <H2>
        {t({
          en: "The ISO certification process, step by step",
          ar: "خطوات الحصول على شهادة الأيزو، خطوة بخطوة",
        })}
      </H2>
      <P>
        {t({
          en: "The sequence below separates preparation from the certification body’s assessment and decision. Confirm the applicable stages, responsibilities and deliverables in your own written scope.",
          ar: "يفصل التسلسل أدناه بين التحضير والتقييم الذي تجريه جهة منح الشهادات وقرارها. أكّد المراحل المنطبقة والمسؤوليات والمخرجات في نطاق عملك المكتوب.",
        })}
      </P>
      <P>
        {t({ en: "See ", ar: "قبل المقارنة بين مقدّمي الخدمة، تعرّف إلى " })}
        <LangLink
          to="/insights/iso-consultant-vs-certification-body-in-iraq"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "who handles ISO preparation and who issues the certificate",
            ar: "دور استشاري الأيزو ودور جهة منح الشهادات",
          })}
        </LangLink>
        {t({ en: " before comparing providers.", ar: "." })}
      </P>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
        <table className="w-full border-collapse">
          <thead className="bg-accent">
            <tr>
              <th className={th}>{t({ en: "Stage", ar: "المرحلة" })}</th>
              <th className={th}>{t({ en: "What happens", ar: "ما الذي يحدث" })}</th>
              <th className={th}>{t({ en: "Output", ar: "المخرج" })}</th>
            </tr>
          </thead>
          <tbody>
            {processSteps.map((row) => (
              <tr key={row.stage.en} className="border-t border-border">
                <td className={`${td} font-semibold text-foreground`}>{t(row.stage)}</td>
                <td className={td}>{t(row.what)}</td>
                <td className={td}>{t(row.output)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2>
        {t({
          en: "How long does ISO certification take in Iraq?",
          ar: "كم تستغرق شهادة الأيزو في العراق؟",
        })}
      </H2>
      <P>
        {t({
          en: "A realistic schedule follows a readiness review and confirmation with the proposed certification body. Consider missing evidence, implementation work, assessment availability and any findings that need closure. Ask who is responsible for each step and how delays or scope changes will be handled.",
          ar: "يُحدَّد الجدول الواقعي بعد مراجعة الجاهزية والتأكيد مع جهة منح الشهادات المقترحة. راعِ الأدلة الناقصة وأعمال التطبيق وتوافر مواعيد التقييم وأي ملاحظات تتطلب المعالجة. اسأل عن المسؤول عن كل خطوة وكيفية التعامل مع التأخير أو تغيّر النطاق.",
        })}
      </P>

      <H2>
        {t({
          en: "Why an accredited certificate matters for tenders",
          ar: "لماذا تهم الشهادة المعتمدة في المناقصات",
        })}
      </H2>
      <P>
        {t({
          en: "Use the recipient’s exact requirement rather than assuming every tender accepts the same certificate. Confirm the standard, scope, issuer and any required accreditation. Check the issuer and relevant accreditation body directly where clarification is needed. Certification does not guarantee tender qualification or contract award.",
          ar: "اعتمد نص متطلب الجهة المستفيدة بدلاً من افتراض قبول الشهادة نفسها في جميع المناقصات. أكّد المواصفة والنطاق والجهة المانحة وأي اعتماد مطلوب. ارجع مباشرةً إلى الجهة المانحة وهيئة الاعتماد المعنية عند الحاجة إلى توضيح. لا تضمن الشهادة التأهل للمناقصة أو الفوز بالعقد.",
        })}
      </P>
      <P>
        {t({ en: "For the distinction, read about ", ar: "لفهم الفرق، اقرأ عن " })}
        <LangLink
          to="/insights/accredited-vs-non-accredited-iso-certificate"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "accredited and non-accredited ISO certificates",
            ar: "شهادات الأيزو المعتمدة وغير المعتمدة",
          })}
        </LangLink>
        {t({
          en: " and confirm what your buyer accepts.",
          ar: " وتأكّد مما تقبله الجهة التي ستقدّم لها الشهادة.",
        })}
      </P>

      <P>
        <a
          href="https://www.iso.org/certification.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary hover:underline"
        >
          {t({
            en: "ISO’s explanation of certification, accreditation and choosing a certification body",
            ar: "توضيح منظمة ISO لمنح الشهادات والاعتماد واختيار جهة منح الشهادات",
          })}
        </a>
      </P>

      <P>
        {t({
          en: "Already have offers? Use our guide to ",
          ar: "هل تلقيت عروضاً بالفعل؟ استفد من دليل ",
        })}
        <LangLink
          to="/insights/compare-iso-certification-quotes-iraq"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "comparing ISO certification quotations",
            ar: "مقارنة عروض أسعار شهادات الأيزو",
          })}
        </LangLink>
        {t({
          en: " to align their scope and included work before deciding.",
          ar: " لتوحيد أساس المقارنة من حيث النطاق والأعمال المشمولة قبل اتخاذ القرار.",
        })}
      </P>

      <H2>
        {t({
          en: "How IraqCert supports your certification project",
          ar: "كيف تدعم عراق سيرت مشروع الحصول على الشهادة",
        })}
      </H2>
      <P>
        {t({
          en: "The agreed service can include requirement review, preparation support and coordination with the proposed certification body. Responsibilities and the issuing route must be confirmed in writing. If your business also needs operator registration, review our separate service for ",
          ar: "قد تشمل الخدمة المتفق عليها مراجعة المتطلب ودعم التحضير والتنسيق مع جهة منح الشهادات المقترحة. يجب تأكيد المسؤوليات ومسار الإصدار كتابياً. وإذا احتاجت شركتك أيضاً إلى التسجيل لدى شركة مشغّلة، فراجع خدمتنا المنفصلة الخاصة بـ",
        })}
        <LangLink
          to="/services/oil-gas-registration"
          className="font-semibold text-primary hover:underline"
        >
          {t({ en: "oil and gas vendor registration", ar: "تسجيل موردي النفط والغاز" })}
        </LangLink>
        {t({
          en: ". Its requirements must be checked with the intended operator.",
          ar: "، مع التحقق من متطلبات الشركة المشغّلة المستهدفة.",
        })}
      </P>
      <P>
        {t({
          en: "If BOC is your intended buyer, review ",
          ar: "إذا كانت شركة نفط البصرة هي الجهة التي تستهدفها، فراجع ",
        })}
        <LangLink
          to="/services/oil-gas-registration/basra-oil-company"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "Basra Oil Company vendor registration",
            ar: "تسجيل الموردين لدى شركة نفط البصرة",
          })}
        </LangLink>
        {t({ en: " as a separate application process.", ar: " باعتباره إجراء تقديم منفصلاً." })}
      </P>
    </>
  );
}
